import React, { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import api from '../../utils/api';

/**
 * Delivery Note print view — A4 layout that mirrors the Sales Invoice
 * print format (TO block + right meta grid + 4-cell strip + items table)
 * but with QUANTITY ONLY (no prices anywhere).
 *
 *  • Top-left  : Company logo (28mm box, height-matched with header)
 *  • Top-right : Uploaded Document Header banner (60mm × 28mm)
 *  • Bottom    : Optional Document Footer banner across full width
 *  • Renders inside a hidden iframe and auto-fires browser Print dialog.
 */

const FALLBACK_NAME = process.env.REACT_APP_COMPANY_NAME || 'Company';
const FALLBACK_LOGO = process.env.REACT_APP_COMPANY_LOGO_URL || '';

const formatDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

function DeliveryNoteSheet({ dn, customer, company, preparedBy, approvers }) {
  const name = company?.name || FALLBACK_NAME;
  const logo = company?.logo_url || FALLBACK_LOGO;
  const headerImg = company?.document_header_url;
  const footerImg = company?.document_footer_url;

  return (
    <>
      <style>{`
        * { box-sizing: border-box; }
        body { margin: 0; font-family: 'Segoe UI', Arial, sans-serif; color: #111; background: #fff; }
        .page { padding: 0; max-width: 100%; font-size: 11px; position: relative; }
        .doc-title { text-align: center; font-size: 15px; font-weight: 700; letter-spacing: 1px; margin: 0 0 6px; }
        .doc-title .ar { display: block; font-size: 11px; font-weight: 600; margin-top: 1px; }

        .meta-grid { display: grid; grid-template-columns: 1.4fr 1fr; gap: 0; border: 1px solid #000; }
        .meta-cell { padding: 4px 6px; border-right: 1px solid #000; }
        .meta-cell:last-child { border-right: 0; }
        .meta-cell .lbl { font-size: 9.5px; color: #444; }
        .meta-cell .ar { font-size: 9px; color: #555; }
        .to-block p { margin: 1px 0; line-height: 1.3; font-size: 10.5px; }
        .to-block .name { font-weight: 700; font-size: 11.5px; }
        .right-meta table { width: 100%; border-collapse: collapse; font-size: 10px; }
        .right-meta td { padding: 2px 4px; border-bottom: 1px dotted #bbb; vertical-align: top; }
        .right-meta td.l { width: 55%; color: #333; }
        .right-meta td.l .ar { display: block; font-size: 9px; color: #777; }
        .right-meta td.v { font-weight: 600; }

        .strip { display: grid; grid-template-columns: repeat(4, 1fr); border: 1px solid #000; border-top: 0; font-size: 10.5px; }
        .strip > div { padding: 3px 6px; border-right: 1px solid #000; }
        .strip > div:last-child { border-right: 0; }
        .strip .lbl { display: block; font-size: 9px; color: #555; text-transform: uppercase; }
        .strip .ar { font-size: 8.5px; color: #777; }
        .strip .val { font-weight: 600; }

        table.items { width: 100%; border-collapse: collapse; margin-top: 6px; }
        table.items th, table.items td { border: 1px solid #000; padding: 3px 5px; font-size: 10.5px; vertical-align: top; line-height: 1.3; }
        table.items th { background: #f3f4f6; text-align: left; font-size: 10px; }
        table.items th .ar { display: block; font-size: 8.5px; color: #555; font-weight: 500; }
        table.items td.num { text-align: right; font-variant-numeric: tabular-nums; }
        table.items td.center { text-align: center; }

        .notes { margin-top: 6px; font-size: 10.5px; padding: 4px 6px; border: 1px solid #ccc; background: #f9fafb; }

        .signs { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-top: auto; padding-top: 24px; }
        .sign-box { display: flex; flex-direction: column; justify-content: flex-end; font-size: 10px; }
        .sign-box .stamp { height: 18mm; display: flex; align-items: flex-end; justify-content: center; margin-bottom: 1.5mm; }
        .sign-box .stamp img { max-height: 18mm; max-width: 90%; object-fit: contain; }
        .sign-box .stamp-dual { gap: 4mm; }
        .sign-box .stamp-dual .stamp-slot { flex: 1; display: flex; align-items: flex-end; justify-content: center; height: 100%; }
        .sign-box .stamp-dual .stamp-slot img { max-height: 18mm; max-width: 100%; object-fit: contain; }
        .sign-box .line { border-top: 1px solid #000; padding-top: 4px; text-align: center; }
        .sign-box .lbl { font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; font-size: 9.5px; color: #555; }
        .sign-box .ar { font-size: 9px; color: #666; }
        .sign-box .date { margin-top: 8px; font-size: 9.5px; color: #555; text-align: center; }

        @page { size: A4; margin: 0; }
        html, body { margin: 0; padding: 0; }
        .page {
          display: flex;
          flex-direction: column;
          min-height: 100vh;
          padding: 50mm 12mm 80mm 12mm;
          box-sizing: border-box;
        }
        /* Header banner — TOP-RIGHT corner, height-matched with left logo. */
        .banner-top, .banner-bottom { position: absolute; z-index: 1; }
        .banner-top {
          top: 8mm; right: 12mm;
          width: 60mm; height: 28mm;
          display: flex; align-items: center; justify-content: flex-end;
        }
        .banner-bottom { bottom: 0; left: 0; right: 0; text-align: center; }
        .banner-top img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; }
        .banner-bottom img { width: 100%; max-height: 22mm; object-fit: contain; display: block; }
        /* Left-corner company logo + name + address — vertically aligned with the right banner. */
        .logo-left {
          position: absolute;
          top: 8mm; left: 12mm;
          width: 86mm;
        }
        .logo-left .logo-box {
          width: 60mm; height: 28mm;
          display: flex; align-items: center; justify-content: flex-start;
        }
        .logo-left .logo-box img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; }
        .logo-left .reg-block { margin-top: 2mm; font-size: 9pt; color: #111; line-height: 1.45; }
        .logo-left .reg-block .lbl { color: #6b7280; }
        .logo-left .reg-block .val { font-weight: 700; color: #111; }
        @media print {
          .banner-bottom { position: fixed !important; bottom: 0 !important; left: 0; right: 0; }
          .signs {
            position: fixed !important;
            bottom: 28mm !important;
            left: 12mm;
            right: 12mm;
            margin-top: 0 !important;
            padding-top: 0 !important;
          }
        }
      `}</style>

      <div className="page">
        {/* TOP-LEFT logo + CR / VAT registration lines.
            Company name + address removed — they belong in the uploaded footer banner. */}
        {(logo || company?.cr_number || company?.cr_number_2 || company?.tax_id) && (
          <div className="logo-left">
            {logo && (
              <div className="logo-box">
                <img src={logo} alt={name} />
              </div>
            )}
            {(company?.cr_number || company?.cr_number_2 || company?.tax_id) && (
              <div className="reg-block">
                {company.cr_number && (
                  <div><span className="lbl">CR No 1:</span> <span className="val">{company.cr_number}</span></div>
                )}
                {company.cr_number_2 && (
                  <div><span className="lbl">CR No 2:</span> <span className="val">{company.cr_number_2}</span></div>
                )}
                {company.tax_id && (
                  <div><span className="lbl">VAT No:</span> <span className="val">{company.tax_id}</span></div>
                )}
              </div>
            )}
          </div>
        )}
        {/* TOP-RIGHT uploaded header banner */}
        {headerImg && (
          <div className="banner-top"><img src={headerImg} alt="" /></div>
        )}
        {/* BOTTOM uploaded footer banner */}
        {footerImg && (
          <div className="banner-bottom"><img src={footerImg} alt="" /></div>
        )}

        <div className="doc-title">
          DELIVERY NOTE
          <span className="ar">إشعار تسليم</span>
        </div>

        {/* TO block + Right meta */}
        <div className="meta-grid">
          <div className="meta-cell to-block">
            <div className="lbl">TO <span className="ar">/ الى</span></div>
            <p className="name">{dn.customer_name}</p>
            {(dn.delivery_address || customer?.address) && <p>{dn.delivery_address || customer.address}</p>}
            {customer?.region && <p>{customer.region}</p>}
            {customer?.phone && <p>Tel: {customer.phone}</p>}
          </div>
          <div className="meta-cell right-meta">
            <table>
              <tbody>
                <tr>
                  <td className="l">DN No <span className="ar">رقم الإشعار</span></td>
                  <td className="v">{dn.dn_number}</td>
                </tr>
                <tr>
                  <td className="l">Date <span className="ar">التاريخ</span></td>
                  <td className="v">{formatDate(dn.dn_date)}</td>
                </tr>
                <tr>
                  <td className="l">Customer PO No <span className="ar">رقم امر الشراء</span></td>
                  <td className="v">{dn.customer_po_no || ''}</td>
                </tr>
                <tr>
                  <td className="l">Customer PO Date <span className="ar">تاريخ امر الشراء</span></td>
                  <td className="v">{dn.customer_po_date ? formatDate(dn.customer_po_date) : ''}</td>
                </tr>
                <tr>
                  <td className="l">Reference <span className="ar">المرجع</span></td>
                  <td className="v">{dn.reference || dn.invoice_number || ''}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Strip: PROJECT | DELIVERED BY | DELIVERY MODE | LOCATION */}
        <div className="strip">
          <div>
            <span className="lbl">Project <span className="ar">المشروع</span></span>
            <span className="val">{dn.project || '-'}</span>
          </div>
          <div>
            <span className="lbl">Delivered By <span className="ar">سُلّم بواسطة</span></span>
            <span className="val">{dn.delivered_by || '-'}</span>
          </div>
          <div>
            <span className="lbl">Delivery Mode <span className="ar">طريقة التسليم</span></span>
            <span className="val">{dn.delivery_mode || '-'}</span>
          </div>
          <div>
            <span className="lbl">Location <span className="ar">الموقع</span></span>
            <span className="val">{customer?.region || dn.location || '-'}</span>
          </div>
        </div>

        {/* Items table — Quantity only, no prices */}
        <table className="items">
          <thead>
            <tr>
              <th style={{ width: 28 }} className="center">#</th>
              <th style={{ width: 90 }}>Part No<span className="ar">رقم الصنف</span></th>
              <th>Product Description<span className="ar">وصف المنتج</span></th>
              <th style={{ width: 50 }} className="center">Unit<span className="ar">الوحدة</span></th>
              <th style={{ width: 50 }} className="num">Qty<span className="ar">الكمية</span></th>
              <th style={{ width: 160 }}>Serial Numbers<span className="ar">الأرقام التسلسلية</span></th>
            </tr>
          </thead>
          <tbody>
            {(dn.items || []).map((it, i) => (
              <tr key={i}>
                <td className="center">{i + 1}</td>
                <td>{it.part_number || '-'}</td>
                <td>
                  <b>{it.product_name}</b>
                  {it.description && (
                    <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>{it.description}</div>
                  )}
                  {it.warranty_months ? (
                    <div style={{ fontSize: 9.5, color: '#666', marginTop: 2 }}>
                      Warranty: {it.warranty_months} months
                    </div>
                  ) : null}
                </td>
                <td className="center">{it.unit || 'Pcs'}</td>
                <td className="num">{it.quantity}</td>
                <td style={{ fontSize: 9.5 }}>{(it.serial_numbers || []).join(', ') || '—'}</td>
              </tr>
            ))}
            {(!dn.items || dn.items.length === 0) && (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#9ca3af' }}>No items</td></tr>
            )}
          </tbody>
        </table>

        {dn.notes && (
          <div className="notes"><b>Notes:</b> {dn.notes}</div>
        )}

        {/* Signatures pinned to bottom */}
        <div className="signs">
          <SigCellDN role="Prepared By" arabic="أعدها" person={preparedBy || (dn.delivered_by ? { name: dn.delivered_by, signature_url: '' } : null)} dateStr={fmtDateDN(dn?.dn_date || dn?.created_at)} />
          <ApprovedCellDN approvers={approvers} />
          <SigCellDN role="Received By" arabic="المستلم" person={null} />
        </div>
      </div>
    </>
  );
}

const fmtDateDN = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(s).slice(0, 10);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const SigCellDN = ({ role, arabic, person, dateStr }) => (
  <div className="sign-box">
    <div className="stamp">
      {person?.signature_url ? <img src={person.signature_url} alt="" /> : null}
    </div>
    <div className="line">
      <div className="lbl">{role} <span className="ar">/ {arabic}</span></div>
    </div>
    <div className="date">Date: {dateStr || '____________________'}</div>
  </div>
);

const ApprovedCellDN = ({ approvers }) => {
  const list = Array.isArray(approvers) ? approvers : [];
  if (list.length <= 1) {
    const p = list[0] || null;
    return <SigCellDN role="Approved By" arabic="اعتمدها" person={p} dateStr={fmtDateDN(p?.at)} />;
  }
  return (
    <div className="sign-box">
      <div className="stamp stamp-dual">
        {list.map((p, i) => (
          <div key={i} className="stamp-slot">
            {p?.signature_url ? <img src={p.signature_url} alt="" /> : null}
          </div>
        ))}
      </div>
      <div className="line">
        <div className="lbl">Approved By <span className="ar">/ اعتمدها</span></div>
      </div>
      <div className="date">
        Date:&nbsp;
        {list.map((p, i) => (
          <span key={i}>
            {i > 0 ? ' · ' : ''}{fmtDateDN(p?.at) || '—'}
          </span>
        ))}
      </div>
    </div>
  );
};

export default function DeliveryNotePrintView({ dn, customer, preparedBy, approvers, onDone }) {
  const launchedRef = useRef(false);
  const [company, setCompany] = useState(null);
  const [companyLoaded, setCompanyLoaded] = useState(false);

  // Pull current company settings (logo, address, header/footer banners).
  useEffect(() => {
    let cancelled = false;
    api.get('/companies/current/settings')
      .then((res) => { if (!cancelled) setCompany(res.data || null); })
      .catch(() => { if (!cancelled) setCompany(null); })
      .finally(() => { if (!cancelled) setCompanyLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!companyLoaded || launchedRef.current) return;
    launchedRef.current = true;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write('<!DOCTYPE html><html><head><title></title></head><body><div id="root"></div></body></html>');
    doc.close();

    const root = createRoot(doc.getElementById('root'));
    root.render(<DeliveryNoteSheet dn={dn} customer={customer} company={company} preparedBy={preparedBy} approvers={approvers} />);

    const fire = () => {
      // Wait for every image in the iframe (logo, header, footer, signatures)
      // to load before firing print. Otherwise the first print can capture a
      // half-rendered layout — only the cached logo shows.
      const idoc = iframe.contentDocument;
      const imgs = idoc ? Array.from(idoc.images || []) : [];
      const waiters = imgs.map((img) => (
        img.complete && img.naturalWidth > 0
          ? Promise.resolve()
          : new Promise((res) => {
              img.addEventListener('load', res, { once: true });
              img.addEventListener('error', res, { once: true });
              setTimeout(res, 4000);
            })
      ));
      Promise.all(waiters).then(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          console.error(e);
        }
        setTimeout(() => {
          try { document.body.removeChild(iframe); } catch (e) { /* noop */ }
          onDone?.();
        }, 500);
      });
    };
    setTimeout(fire, 200);
  }, [companyLoaded, dn, customer, company, preparedBy, approvers, onDone]);

  return null;
}
